import { z } from 'zod';
import {
  ASSIGNABLE_MODULES,
  assignableModuleSchema,
  assignableRoleSchema,
  roleSchema,
  type AppModule,
} from '../domain/enums';

export const loginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).max(200),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const refreshRequestSchema = z.object({
  refreshToken: z.string().min(10),
});
export type RefreshRequest = z.infer<typeof refreshRequestSchema>;

export interface AuthenticatedUser {
  id: string;
  email: string;
  name: string;
  role: z.infer<typeof roleSchema>;
  /** Acesso limitado: cria/gerencia tenants próprios; runs com caps. */
  isTester: boolean;
  /** Tenants que o usuário enxerga. Owner ignora a lista e vê todos. */
  tenantIds: string[];
  /** Módulos operacionais. Owner é tratado como se tivesse todos. */
  modules: AppModule[];
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: AuthenticatedUser;
}

export const createUserRequestSchema = z.object({
  email: z.string().email(),
  name: z.string().min(2).max(120),
  password: z.string().min(8).max(200),
  role: assignableRoleSchema,
  isTester: z.boolean().default(false),
  tenantIds: z.array(z.string().uuid()).default([]),
  modules: z.array(assignableModuleSchema).default([]),
});
export type CreateUserRequest = z.infer<typeof createUserRequestSchema>;

export const updateUserRequestSchema = z.object({
  name: z.string().min(2).max(120).optional(),
  role: assignableRoleSchema.optional(),
  isActive: z.boolean().optional(),
  isTester: z.boolean().optional(),
});
export type UpdateUserRequest = z.infer<typeof updateUserRequestSchema>;

export const replaceUserTenantsSchema = z.object({
  tenantIds: z.array(z.string().uuid()),
});
export type ReplaceUserTenantsRequest = z.infer<typeof replaceUserTenantsSchema>;

export const replaceUserModulesSchema = z.object({
  modules: z.array(assignableModuleSchema),
});
export type ReplaceUserModulesRequest = z.infer<typeof replaceUserModulesSchema>;

export interface UserSummary {
  id: string;
  email: string;
  name: string;
  role: z.infer<typeof roleSchema>;
  isActive: boolean;
  isTester: boolean;
  tenantIds: string[];
  modules: AppModule[];
  lastLoginAt: string | null;
  createdAt: string;
}

export const ALL_ASSIGNABLE_MODULES: AppModule[] = [...ASSIGNABLE_MODULES];

/** Provedores de enriquecimento (sem `mock` — mock só via env da plataforma). */
export const llmProviderSchema = z.enum(['openai', 'anthropic', 'openai-compatible']);
export type LlmProviderName = z.infer<typeof llmProviderSchema>;

/** @deprecated Use `llmProviderSchema`. */
export const userLlmProviderSchema = llmProviderSchema;
/** @deprecated Use `LlmProviderName`. */
export type UserLlmProvider = LlmProviderName;

/** Resposta da API: nunca inclui a API key em claro, só o estado. */
export const llmConfigViewSchema = z.object({
  enabled: z.boolean(),
  provider: llmProviderSchema,
  baseUrl: z.string(),
  model: z.string(),
  hasApiKey: z.boolean(),
});
export type LlmConfigView = z.infer<typeof llmConfigViewSchema>;

/** Config LLM por tenant (owner). Mesmo shape da view legada por usuário. */
export type TenantLlmConfig = LlmConfigView;
/** @deprecated Prefer `TenantLlmConfig`. */
export type UserLlmConfig = LlmConfigView;
/** @deprecated Prefer `llmConfigViewSchema`. */
export const userLlmConfigSchema = llmConfigViewSchema;
export const tenantLlmConfigSchema = llmConfigViewSchema;

export const DEFAULT_TENANT_LLM_CONFIG: TenantLlmConfig = {
  enabled: false,
  provider: 'anthropic',
  baseUrl: '',
  model: '',
  hasApiKey: false,
};
/** @deprecated Prefer `DEFAULT_TENANT_LLM_CONFIG`. */
export const DEFAULT_USER_LLM_CONFIG = DEFAULT_TENANT_LLM_CONFIG;

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Nomes de `LLM_PROVIDER` — não são IDs de modelo válidos no body da API. */
const RESERVED_LLM_PROVIDER_NAMES = new Set([
  'openai-compatible',
  'openai',
  'anthropic',
  'mock',
]);

/**
 * Escrita da configuração de LLM por tenant (owner da ferramenta).
 *
 * Com `enabled=true`, modelo é obrigatório. URL http(s) é obrigatória só para
 * `openai-compatible`; `openai` e `anthropic` podem omitir (defaults no worker).
 * Campo `apiKey` vazio preserva a chave já cifrada; `clearApiKey` remove.
 */
export const updateTenantLlmConfigSchema = z
  .object({
    enabled: z.boolean(),
    provider: llmProviderSchema,
    baseUrl: z.string().trim().max(500),
    model: z.string().trim().max(200),
    apiKey: z
      .string()
      .max(500)
      .optional()
      .transform((value) => {
        const trimmed = value?.trim() ?? '';
        return trimmed.length === 0 ? undefined : trimmed;
      }),
    clearApiKey: z.boolean().optional().default(false),
  })
  .superRefine((value, ctx) => {
    if (!value.enabled) return;

    const requiresBaseUrl = value.provider === 'openai-compatible';
    if (requiresBaseUrl || value.baseUrl.length > 0) {
      if (!isHttpUrl(value.baseUrl)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['baseUrl'],
          message: 'Informe uma URL http(s) válida',
        });
      }
    }

    if (value.model.length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['model'],
        message: 'Modelo é obrigatório quando a IA da conta está ativa',
      });
    } else if (RESERVED_LLM_PROVIDER_NAMES.has(value.model.toLowerCase())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['model'],
        message:
          'Informe o ID do modelo (ex.: claude-sonnet-4-6), não o nome do adapter',
      });
    }
  });
export type UpdateTenantLlmConfig = z.infer<typeof updateTenantLlmConfigSchema>;
/** @deprecated Prefer `UpdateTenantLlmConfig` / `updateTenantLlmConfigSchema`. */
export const updateUserLlmConfigSchema = updateTenantLlmConfigSchema;
/** @deprecated Prefer `UpdateTenantLlmConfig`. */
export type UpdateUserLlmConfig = UpdateTenantLlmConfig;
